import React, { useEffect, useMemo, useState } from 'react';
import {
  Calendar,
  CheckCircle2,
  Target,
  Clock,
  MessagesSquare,
  ChevronDown,
  ChevronRight,
  SlidersHorizontal,
  Upload,
  Phone,
  MessageSquare,
  Mail,
  X,
  Bot,
} from 'lucide-react';
import { Card, Badge, PageTitle } from './ui';
import {
  logsAPI,
  timeAgo,
  effectiveDurationMs,
  formatDuration,
  transcriptLines,
  logChannel,
  logStatus,
} from './api';
import { LoadingRows, ErrorNote } from './DemoCustomerAIPages';

const PAGE_SIZE = 10;

const CHANNEL_ICONS = {
  phone: { icon: Phone, tint: 'text-teal-600 bg-teal-50' },
  sms: { icon: MessageSquare, tint: 'text-violet-600 bg-violet-50' },
  chat: { icon: MessagesSquare, tint: 'text-sky-600 bg-sky-50' },
  email: { icon: Mail, tint: 'text-orange-500 bg-orange-50' },
};

const customerLabel = (log) => {
  const c = log?.crmEntry?.contact;
  if (c) {
    const name = c.name || [c.firstName, c.lastName].filter(Boolean).join(' ');
    if (name) return name;
  }
  if (log?.to && typeof log.to === 'string') return log.to;
  if (log?.from && typeof log.from === 'string') return log.from;
  return logChannel(log) === 'chat' ? 'Web visitor' : 'Unknown caller';
};

const customerContact = (log) => {
  const c = log?.crmEntry?.contact;
  return c?.phone || c?.email || log?.callSid || '';
};

const initials = (name) =>
  String(name)
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';

const lastMessage = (log) => {
  if (log?.summary) return log.summary;
  if (Array.isArray(log?.messages) && log.messages.length) {
    return String(log.messages[log.messages.length - 1]?.content || '');
  }
  const lines = transcriptLines(log);
  return lines.length ? lines[lines.length - 1].text : '—';
};

const DETAIL_TABS = ['Conversation', 'Details'];

/* ---------- detail drawer ---------- */

const DetailDrawer = ({ log, onClose }) => {
  const [tab, setTab] = useState('Conversation');
  const status = logStatus(log);
  const name = customerLabel(log);
  const lines = transcriptLines(log);

  return (
    <div className="fixed bottom-0 right-0 top-[72px] z-40 flex w-[420px] flex-col border-l border-slate-200 bg-white shadow-2xl">
      <div className="border-b border-slate-100 px-6 pb-3 pt-5">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <h2 className="truncate text-[19px] font-bold">{name}</h2>
            <div className="mt-1 flex items-center gap-2.5">
              <span className="truncate text-[13px] text-slate-500">{customerContact(log)}</span>
              <Badge tone={status.tone}>{status.label}</Badge>
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>
        <div className="mt-4 flex items-center gap-5 text-[13px] font-medium">
          {DETAIL_TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`-mb-3 border-b-2 pb-2.5 transition-colors ${
                tab === t ? 'border-teal-600 text-teal-700' : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* meta */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 border-b border-slate-100 px-6 py-4 text-[12.5px]">
        <div className="flex items-center gap-2">
          <Bot size={14} className="text-teal-600" />
          <span>
            <span className="block text-slate-400">AI Agent</span>
            <span className="font-semibold">{log.scriptName || '—'}</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Clock size={14} className="text-slate-400" />
          <span>
            <span className="block text-slate-400">Duration</span>
            <span className="font-semibold">{formatDuration(effectiveDurationMs(log))}</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Phone size={14} className="text-slate-400" />
          <span>
            <span className="block text-slate-400">Channel</span>
            <span className="font-semibold capitalize">{logChannel(log)}</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Calendar size={14} className="text-slate-400" />
          <span>
            <span className="block text-slate-400">Started</span>
            <span className="font-semibold">
              {new Date(log.callStartTime || log.createdAt).toLocaleString()}
            </span>
          </span>
        </div>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
        {tab === 'Conversation' ? (
          lines.length ? (
            lines.map(({ from, text }, i) =>
              from === 'ai' ? (
                <div key={i} className="flex items-start gap-2.5">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-teal-50 text-teal-600">
                    <Bot size={13} />
                  </span>
                  <div className="max-w-[290px] rounded-2xl rounded-tl-sm bg-teal-50/70 px-3.5 py-2.5 text-[12.5px] leading-relaxed text-slate-700">
                    {text}
                  </div>
                </div>
              ) : (
                <div key={i} className="flex items-start justify-end gap-2.5">
                  <div className="max-w-[290px] rounded-2xl rounded-tr-sm bg-slate-100 px-3.5 py-2.5 text-[12.5px] leading-relaxed text-slate-700">
                    {text}
                  </div>
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-500">
                    {initials(name)}
                  </span>
                </div>
              )
            )
          ) : (
            <p className="pt-10 text-center text-[13px] text-slate-400">No transcript recorded.</p>
          )
        ) : (
          <div className="space-y-3 text-[13px]">
            {[
              ['Outcome', log.outcome || '—'],
              ['Mood', log.mood || '—'],
              ['Call Type', log.callType || '—'],
              ['Direction', log.callDirection || '—'],
              ['Call SID', log.callSid || '—'],
              ['Recording', log.recordingAvailable ? 'Available' : 'Not available'],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-6 border-b border-slate-50 pb-2.5">
                <span className="text-slate-400">{k}</span>
                <span className="truncate font-semibold capitalize">{String(v)}</span>
              </div>
            ))}
            {log.summary && (
              <div className="pt-1">
                <span className="text-slate-400">Summary</span>
                <p className="mt-1.5 rounded-xl bg-slate-50 p-3.5 leading-relaxed text-slate-700">{log.summary}</p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-slate-100 px-6 py-3 text-[11.5px] text-slate-400">
        <span className="truncate">ID: {log._id}</span>
        <button
          onClick={() => navigator.clipboard?.writeText(log._id)}
          className="shrink-0 font-semibold text-teal-700 hover:text-teal-800"
        >
          Copy ID
        </button>
      </div>
    </div>
  );
};

/* ---------- page ---------- */

const FilterButton = ({ children }) => (
  <button className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-[13px] font-medium shadow-sm hover:bg-slate-50">
    {children}
  </button>
);

const DemoLogs = () => {
  const [logs, setLogs] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    logsAPI
      .list()
      .then((res) => setLogs(res.data.logs || []))
      .catch((e) => setError(e.response?.data?.error || e.message));
  }, []);

  const kpis = useMemo(() => {
    if (!logs) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayLogs = logs.filter((l) => new Date(l.createdAt) >= today);
    const completed = logs.filter((l) => (l.outcome || '').toLowerCase() === 'success');
    const analyzed = logs.filter((l) => l.outcome);
    const durations = logs.map(effectiveDurationMs).filter(Boolean);
    const avgMs = durations.length ? durations.reduce((a, b) => a + b, 0) / durations.length : 0;
    return [
      { label: 'Total Conversations', value: logs.length.toLocaleString(), icon: MessagesSquare, tint: 'bg-teal-50 text-teal-600' },
      { label: 'Today', value: todayLogs.length.toLocaleString(), icon: Calendar, tint: 'bg-violet-50 text-violet-600' },
      {
        label: 'Successful',
        value: completed.length.toLocaleString(),
        sub: logs.length ? `${Math.round((completed.length / logs.length) * 100)}% of total` : '',
        icon: CheckCircle2,
        tint: 'bg-emerald-50 text-emerald-600',
      },
      {
        label: 'Resolution Rate',
        value: analyzed.length ? `${Math.round((completed.length / analyzed.length) * 100)}%` : '—',
        sub: 'of analyzed calls',
        icon: Target,
        tint: 'bg-sky-50 text-sky-600',
      },
      { label: 'Avg. Duration', value: formatDuration(avgMs), icon: Clock, tint: 'bg-orange-50 text-orange-500' },
    ];
  }, [logs]);

  const pageCount = logs ? Math.max(1, Math.ceil(logs.length / PAGE_SIZE)) : 1;
  const rows = logs ? logs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) : [];

  return (
    <div className={`flex h-full flex-col transition-all ${selected ? 'pr-[420px]' : ''}`}>
      <PageTitle
        title="Conversations"
        subtitle="Monitor and manage all conversations across your Customer AI agents."
      />

      {error && <div className="mt-6"><ErrorNote error={error} /></div>}
      {!error && logs === null && <LoadingRows label="Loading conversations…" />}

      {!error && logs !== null && (
        <>
          {/* KPIs */}
          <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
            {kpis.map(({ label, value, sub, icon: Icon, tint }) => (
              <Card key={label} className="p-4">
                <div className="flex items-center gap-2.5">
                  <span className={`flex h-8 w-8 items-center justify-center rounded-full ${tint}`}>
                    <Icon size={15} strokeWidth={2} />
                  </span>
                  <span className="text-[12.5px] font-medium text-slate-500">{label}</span>
                </div>
                <div className="mt-2.5 text-[24px] font-bold tracking-tight">{value}</div>
                {sub && <div className="mt-0.5 text-[11.5px] font-medium text-slate-400">{sub}</div>}
              </Card>
            ))}
          </div>

          {/* Filters */}
          <div className="mt-5 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <FilterButton>
                All AI Agents <ChevronDown size={14} className="text-slate-400" />
              </FilterButton>
              <FilterButton>
                All Channels <ChevronDown size={14} className="text-slate-400" />
              </FilterButton>
              <FilterButton>
                All Statuses <ChevronDown size={14} className="text-slate-400" />
              </FilterButton>
              <FilterButton>
                <SlidersHorizontal size={14} className="text-slate-400" /> More Filters
              </FilterButton>
            </div>
            <FilterButton>
              <Upload size={14} className="text-slate-400" /> Export
            </FilterButton>
          </div>

          {/* Table — the list scrolls, header/KPIs/filters stay fixed */}
          <Card className="mt-4 flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="min-h-0 flex-1 overflow-y-auto">
            <table className="w-full text-left">
              <thead className="sticky top-0 z-10 bg-white">
                <tr className="border-b border-slate-100 text-[12px] font-medium text-slate-400">
                  <th className="px-6 py-3.5 font-medium">Customer</th>
                  <th className="px-4 py-3.5 font-medium">AI Agent</th>
                  <th className="px-4 py-3.5 font-medium">Channel</th>
                  <th className="px-4 py-3.5 font-medium">Status</th>
                  <th className="px-4 py-3.5 font-medium">Last Message</th>
                  <th className="px-4 py-3.5 font-medium">Time</th>
                  <th className="px-4 py-3.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {rows.map((log) => {
                  const channel = CHANNEL_ICONS[logChannel(log)];
                  const status = logStatus(log);
                  const name = customerLabel(log);
                  return (
                    <tr
                      key={log._id}
                      onClick={() => setSelected(log)}
                      className={`cursor-pointer transition-colors hover:bg-slate-50/60 ${
                        selected?._id === log._id ? 'bg-teal-50/40' : ''
                      }`}
                    >
                      <td className="px-6 py-3.5">
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[11px] font-bold text-slate-500">
                            {initials(name)}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-[13.5px] font-semibold">{name}</p>
                            <p className="max-w-[160px] truncate text-[12px] text-slate-500">{customerContact(log)}</p>
                          </div>
                        </div>
                      </td>
                      <td className="max-w-[170px] truncate px-4 py-3.5 text-[13px]">{log.scriptName || '—'}</td>
                      <td className="px-4 py-3.5">
                        <span className={`flex h-7 w-7 items-center justify-center rounded-full ${channel.tint}`}>
                          <channel.icon size={13.5} />
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <Badge tone={status.tone}>{status.label}</Badge>
                      </td>
                      <td className="max-w-[260px] px-4 py-3.5">
                        <span className="block truncate text-[12.5px] text-slate-600">{lastMessage(log)}</span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3.5 text-[12.5px] text-slate-500">
                        {timeAgo(log.createdAt)}
                      </td>
                      <td className="px-4 py-3.5">
                        <ChevronRight size={16} className="text-slate-300" />
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-[13.5px] text-slate-400">
                      No conversations yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>

            {/* pagination */}
            {logs.length > PAGE_SIZE && (
              <div className="flex items-center justify-between border-t border-slate-100 px-6 py-3.5">
                <span className="text-[12.5px] text-slate-500">
                  Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, logs.length)} of{' '}
                  {logs.length.toLocaleString()} conversations
                </span>
                <div className="flex items-center gap-1.5">
                  {Array.from({ length: Math.min(pageCount, 7) }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`h-8 min-w-8 rounded-lg px-2 text-[13px] font-medium ${
                        p === page ? 'bg-teal-50 text-teal-700' : 'text-slate-500 hover:bg-slate-100'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  {pageCount > 7 && <span className="px-1 text-[13px] text-slate-400">… {pageCount}</span>}
                  <button
                    onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                    className="h-8 rounded-lg px-2 text-slate-400 hover:bg-slate-100"
                  >
                    <ChevronRight size={15} />
                  </button>
                </div>
              </div>
            )}
          </Card>
        </>
      )}

      {selected && <DetailDrawer log={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

export default DemoLogs;
